<template>
    <div ref="container" style="min-height: 80vh">
        <h2>{{ $t('menu.new_measurement') }}</h2>
        <!-- Inspection template selector -->
        <div v-if="!inspectionTemplate" class="nopads">
            <Transition name="fade">
                <mvr-inspection-templates-list :loadInspectables="true" :disableAdd="true" @selected="setTemplate" />
            </Transition>
        </div>
        <div class="col-sm-12" v-if="!!inspectionTemplate">
            <!-- Contract selector -->
            <Transition name="fade" v-if="!inspection.contract">
                <inspection-contract-selector @onContractSelected="setContract" />
            </Transition>

            <!-- Inspection form -->
            <div v-if="inspection.contract">
                <div class="row nopads">
                    <div class="col-sm-6 form-data">
                        <span class="span-title">{{ $t('mvr_inspection.inspection_editor.order') }}</span>
                        <contract-order-multiselect
                            ref="orderFilter"
                            :contract-ids="inspection.contract ? [inspection.contract.id] : []"
                            :single-mode="true"
                            @orders-selected="setOrder"
                        />
                    </div>
                </div>
                <div class="row nopads">
                    <div class="col-sm-12 form-data">
                        <span class="span-title">{{ $t('mvr_inspection.inspection_editor.site') }}</span>
                        <textarea v-model="inspection.site" class="form-control form-control-mb" rows="2"
                            :placeholder="$t('mvr_inspection.inspection_editor.site_notes')"></textarea>
                    </div>
                </div>
                <!-- Sections -->
                <div class="row px-3" v-for="(inspectionSection, index) in inspectionTemplate.sections"
                    v-bind:key="`${inspectionSection.id}-${index}`">
                    <div class="col-sm-12">
                        <span class="item-detail-title">{{ inspectionSection.section_header }}</span>
                        <div class="row nopads inspection-row">
                            <!-- Inspectables -->
                            <div class="col-sm-12 px-2 mt-3"
                                v-for="(inspectable, index) in inspectionSection.inspectables"
                                v-bind:key="`${inspectable.id}-${index}`">
                                <span class="span-title pl-3" style="font-size: 1em">{{ inspectable.inspectable.text }}</span>
                                <span class="span-title pl-3" style="color: #5f8f01">{{ $t('mvr_inspection.inspection_editor.pass') }}</span>
                                <div class="col-12 nopads mt-2 mb-2 pl-3 pr-3">
                                    <input v-model.number="getResult(inspectionSection.id, inspectable).pass"
                                        class="form-control" type="number" min="0" />
                                </div>
                                <div class="col-12 nopads mt-2 mb-2 pl-3 pr-3">
                                    <textarea v-model="getResult(inspectionSection.id, inspectable).pass_note"
                                        class="form-control form-control-mb" rows="2" :placeholder="$t('mvr_inspection.inspection_editor.notes')"></textarea>
                                </div>
                                <hr />
                                <span class="span-title pl-3" style="color: #9e0c0c">{{ $t('mvr_inspection.inspection_editor.fail') }}</span>
                                <div class="col-12 nopads mt-2 mb-2 pl-3 pr-3">
                                    <input v-model.number="getResult(inspectionSection.id, inspectable).fails"
                                        class="form-control" type="number" min="0" />
                                </div>
                                <div class="col-12 nopads mt-2 mb-2 pl-3 pr-3">
                                    <textarea v-model="getResult(inspectionSection.id, inspectable).fail_note"
                                        class="form-control form-control-mb" rows="2" :placeholder="$t('mvr_inspection.inspection_editor.notes')"></textarea>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-sm-12 button-container">
                    <button class="btn form-button btn-success" :disabled="loading" @click.stop="askForSubmission">
                        {{ $t('common.send') }}
                    </button>
                </div>
            </div>
        </div>
        <div class="inspection-back-btn">
            <button class="inspection-back-btn-shadow btn btn-primary" @click.stop="goBack">
                <font-awesome-icon icon="arrow-left" />
            </button>
        </div>
        <base-modal id="submit_modal" v-if="submitModal" :hide-header=true :hide-footer=true @ok="submitInspection">
            <h2 class="editor-header">{{ $t('ins_wizard.submit_confirm') }}</h2>
            <span>{{ $t('ins_wizard.submit_confirm_info') }}</span>
            <div class="col-12 d-flex justify-content-end">
                <button class="btn form-button btn-secondary btn-sm" @click="() => submitModal = false">
                    {{ $t('common.no') }}
                </button>
                <button :disabled="loading" class="btn form-button btn-success btn-sm" @click="submitInspection">
                    {{ $t('common.yes') }}
                </button>
            </div>
        </base-modal>
    </div>
</template>

<script>
import { restApi } from '@/components/mixins/RestApiMixin'
import { timeUtils } from "@/components/mixins/TimeUtils";
import MvrInspectionTemplatesList from '@/components/inspections/mvrInspections/MvrInspectionTemplatesList'
import InspectionContractSelector from "@/components//inspections/wizard/InspectionContractSelector"
import BaseModal from "@/components/BaseModal";
import {inject} from "vue";
import ContractOrderMultiselect from "../../order/ContractOrderMultiselect.vue";

export default {
    name: 'MvrInspectionWizard',
    components: {
        ContractOrderMultiselect,
        BaseModal,
        MvrInspectionTemplatesList,
        InspectionContractSelector
    },
    mixins: [restApi, timeUtils],
    props: {
        user: {
            type: Object,
            default: null
        }
    },
    data: function () {
        return {
            loading: false,
            submitModal: false,
            inspectionTemplate: null,
            inspection: {
                name: '',
                contract: null,
                order: null,
                site: '',
                results: []
            },
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert')
        return { showAlert }
    },
    methods: {
        setContract: function (contract) {
            this.inspection.contract = contract
        },
        setOrder(order) {
            this.inspection.order = Array.isArray(order) ? null : order
        },
        setTemplate(template) {
            const results = []
            template.sections.forEach(section => {
                section.inspectables.forEach(inspectable => {
                    results.push({
                        id: inspectable.id,
                        section: { id: section.id },
                        inspectable: { id: inspectable.inspectable.id },
                        pass_note: '',
                        pass: 0,
                        fail_note: '',
                        fails: 0
                    })
                })
            })

            this.inspection.name = template.name
            this.inspection.results = results
            this.inspectionTemplate = template
        },
        goBack() {
            // Back to contract selection
            if (this.inspection.contract) {
                this.inspection.contract = null
                return
            }
            // Back to template selection
            if (this.inspectionTemplate) {
                this.inspectionTemplate = null
                this.resetInspection()
                return
            }
            // Close the wizard
            this.$emit('close')
        },
        resetInspection() {
            this.inspection = {
                name: '',
                contract: null,
                order: null,
                results: []
            }
        },
        getResult(sectionId, inspectable) {
            let result = this.inspection.results.find(r => r.section.id === sectionId && r.inspectable.id === inspectable.inspectable.id);
            if (!result) {
                // If no matching result is found, create a default one and add it
                result = {
                    id: inspectable.id,
                    section: { id: sectionId },
                    inspectable: { id: inspectable.inspectable.id },
                    pass_note: '',
                    pass: 0,
                    fail_note: '',
                    fails: 0
                };
                this.inspection.results.push(result);
            }
            return result;
        },
        askForSubmission() {
            this.submitModal = true
        },
        submitInspection: function () {
            this.loading = true
            this.restAdd(this.mvrInspectionUrl, this.inspection, () => {
                this.loading = false
                this.$emit('closeAndUpdate')
                this.showAlert(this.$t('mvr_inspection.inspection_editor.create_success'), 'success')
            }, () => {
                this.loading = false
                this.showAlert(this.$t('mvr_inspection.inspection_editor.create_failed'))
            })
        }
    }
}
</script>
